import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';

const EditSponsor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [photoUrl, setPhotoUrl] = useState('');
  
  const [applicantData, setApplicantData] = useState({
    fullName: '',
    passportNo: '',
    gender: '',
    occupation: '',
    dateOfIssue: '',
    dateOfExpiry: '',
    placeOfBirth: '',
    dateOfBirth: '',
    applicationDate: '',
    religion: '',
    maritalStatus: '',
    salary: '',
    qualification: '',
    photoKey: ''
  });

  const [sponsorData, setSponsorData] = useState({
    visaNo: '',
    sponsorID: '',
    sponsorName: '',
    sponsorAddress: '',
    sponsorNameArabic: '',
    sponsorPhone: '',
    email: '',
    memo: ''
  });

  useEffect(() => {
    fetchApplicantData();
    return () => {
      // Clean up the URL when component unmounts
      if (photoUrl) {
        setPhotoUrl('');
      }
    };
  }, [id]);

  const fetchPhotoFromB2 = async (photoKey) => {
    try {
      if (!photoKey) {
        console.log('No photo key provided');
        return null;
      }

      const response = await api.get(`/applicants/file-url?fileKey=${encodeURIComponent(photoKey)}`);
      
      if (response.data && response.data.url) {
        return response.data.url;
      } else {
        console.error('No URL returned from B2 service');
        return null;
      }
    } catch (error) {
      console.error('Error fetching photo from B2:', error);
      return null;
    }
  };

  const fetchApplicantData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/applicants/${id}`);
      const data = response.data;
      
      setApplicantData({
        fullName: data.fullName || '',
        passportNo: data.passportNo || '',
        gender: data.gender || '',
        occupation: data.occupation || '',
        dateOfIssue: data.pasportIssuedDate ? new Date(data.pasportIssuedDate).toLocaleDateString() : '',
        dateOfExpiry: data.pasportExpiryDate ? new Date(data.pasportExpiryDate).toLocaleDateString() : '',
        placeOfBirth: data.placeOfBirth || '',
        dateOfBirth: data.dateOfBirth ? new Date(data.dateOfBirth).toLocaleDateString() : '',
        applicationDate: data.applicationDate ? new Date(data.applicationDate).toLocaleDateString() : '',
        religion: data.religion || '',
        maritalStatus: data.maritalStatus || '',
        salary: data.salary || '',
        qualification: data.qualification || '',
        photoKey: data.photoKey || ''
      });

      setSponsorData({
        visaNo: data.visaNo || '',
        sponsorID: data.sponsorID || '',
        sponsorName: data.sponsorName || '',
        sponsorAddress: data.sponsorAddress || '',
        sponsorNameArabic: data.sponsorNameArabic || '',
        sponsorPhone: data.sponsorPhone || '',
        email: data.sponsorEmail || '',
        memo: data.memo || ''
      });

      // Handle photo from Backblaze B2 using PhotoKey
      if (data.photoKey) {
        try {
          const b2PhotoUrl = await fetchPhotoFromB2(data.photoKey);
          if (b2PhotoUrl) {
            setPhotoUrl(b2PhotoUrl);
          } else {
            console.warn('Could not load photo from B2');
            setPhotoUrl('');
          }
        } catch (error) {
          console.error('Error processing photo from B2:', error);
          setPhotoUrl('');
        }
      } else {
        setPhotoUrl('');
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching applicant data:', err);
      setError(err.response?.data || 'Failed to load applicant data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSponsorChange = (e) => {
    const { name, value } = e.target;
    setSponsorData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      await api.put(`/applicants/${id}/sponsor`, sponsorData);
      setSuccess(true);
      navigate('/applicants/agent_applicants');
    } catch (err) {
      console.error('Error updating sponsor:', err);
      setError(err.response?.data?.error || 'Failed to update sponsor information. Please try again.');
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/applicants/agent_applicants');
  };

  if (loading) {
    return (
      <div className="edit-sponsor-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading applicant information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-sponsor-container">
      <div className="edit-sponsor-card">
        {/* Header - Compact */}
        <div className="edit-sponsor-header">
          <div className="header-left">
            <h3>
              <span className="header-icon">✏️</span>
              Edit Sponsor Information
            </h3>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            {typeof error === 'string' ? error : 'An error occurred. Please try again.'}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            <span className="alert-icon">✅</span>
            Sponsor information updated successfully!
          </div>
        )}

        <form onSubmit={handleSubmit} className="edit-sponsor-form">
          {/* Profile Section */}
          <div className="profile-section">
            <div className="profile-header">
              <div className="profile-avatar">
                {photoUrl ? (
                  <img 
                    src={photoUrl} 
                    alt={applicantData.fullName}
                    className="profile-image"
                    onError={(e) => {
                      console.error('Error loading photo from B2');
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      // Show placeholder if image fails to load
                      const placeholder = e.target.parentElement.querySelector('.profile-placeholder');
                      if (placeholder) {
                        placeholder.style.display = 'flex';
                      }
                    }}
                  />
                ) : (
                  <div className="profile-placeholder">
                    <span>👤</span>
                  </div>
                )}
              </div>
              <div className="profile-info">
                <h3>{applicantData.fullName}</h3>
                <div className="profile-details">
                  <span className="detail-item">{applicantData.passportNo || 'N/A'}</span>
                  <span className="detail-separator">•</span>
                  <span className="detail-item">{applicantData.occupation || 'N/A'}</span>
                  <span className="detail-separator">•</span>
                  <span className="detail-item">{applicantData.gender || 'N/A'}</span>
                </div>
              </div>
            </div>
            
            {/* Applicant Reference - 3 Column Layout */}
            <div className="profile-reference">
              <div className="ref-item">
                <span className="ref-label">DATE OF ISSUE :</span>
                <span className="ref-value">{applicantData.dateOfIssue}</span>
              </div>
              <div className="ref-item">
                <span className="ref-label">DATE OF EXPIRY :</span>
                <span className="ref-value">{applicantData.dateOfExpiry}</span>
              </div>
              <div className="ref-item">
                <span className="ref-label">PLACE OF BIRTH :</span>
                <span className="ref-value">{applicantData.placeOfBirth}</span>
              </div>
              <div className="ref-item">
                <span className="ref-label">DATE OF BIRTH :</span>
                <span className="ref-value">{applicantData.dateOfBirth}</span>
              </div>
              <div className="ref-item">
                <span className="ref-label">APPLICATION DATE :</span>
                <span className="ref-value">{applicantData.applicationDate}</span>
              </div>
              <div className="ref-item">
                <span className="ref-label">RELIGION :</span>
                <span className="ref-value">{applicantData.religion}</span>
              </div>
              <div className="ref-item">
                <span className="ref-label">MARITAL STATUS :</span>
                <span className="ref-value">{applicantData.maritalStatus}</span>
              </div>
              <div className="ref-item">
                <span className="ref-label">SALARY :</span>
                <span className="ref-value">{applicantData.salary}</span>
              </div>
              <div className="ref-item">
                <span className="ref-label">QUALIFICATION :</span>
                <span className="ref-value">{applicantData.qualification || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Sponsor Information Section - Horizontal Label:Field */}
          <div className="section">
            <div className="section-header">
              <h3 className="section-title">
                <span className="section-icon">🏢</span>
                Sponsor Information
              </h3>
              <span className="section-badge section-badge-edit">Edit</span>
            </div>
            <div className="sponsor-grid">
              {/* Row 1 - Column 1 */}
              <div className="form-group">
                <label htmlFor="visaNo">Visa Number</label>
                <input
                  type="text"
                  id="visaNo"
                  name="visaNo"
                  className="form-input"
                  value={sponsorData.visaNo}
                  onChange={handleSponsorChange}
                  placeholder="Enter visa number"
                />
              </div>

              {/* Row 1 - Column 2 */}
              <div className="form-group">
                <label htmlFor="sponsorID">Sponsor ID</label>
                <input
                  type="text"
                  id="sponsorID"
                  name="sponsorID"
                  className="form-input"
                  value={sponsorData.sponsorID}
                  onChange={handleSponsorChange}
                  placeholder="Enter sponsor ID"
                />
              </div>

              {/* Row 1 - Column 3 */}
              <div className="form-group">
                <label htmlFor="sponsorName">Sponsor Name</label>
                <input
                  type="text"
                  id="sponsorName"
                  name="sponsorName"
                  className="form-input"
                  value={sponsorData.sponsorName}
                  onChange={handleSponsorChange}
                  placeholder="Enter sponsor name"
                />
              </div>

              {/* Row 2 - Column 1 */}
              <div className="form-group">
                <label htmlFor="sponsorAddress">Sponsor Address</label>
                <input
                  type="text"
                  id="sponsorAddress"
                  name="sponsorAddress"
                  className="form-input"
                  value={sponsorData.sponsorAddress}
                  onChange={handleSponsorChange}
                  placeholder="Enter sponsor address"
                />
              </div>

              {/* Row 2 - Column 2 */}
              <div className="form-group">
                <label htmlFor="sponsorNameArabic">Sponsor Name (Arabic)</label>
                <input
                  type="text"
                  id="sponsorNameArabic"
                  name="sponsorNameArabic"
                  className="form-input input-rtl"
                  value={sponsorData.sponsorNameArabic}
                  onChange={handleSponsorChange}
                  placeholder="أدخل اسم الكفيل بالعربية"
                  dir="rtl"
                />
              </div>

              {/* Row 2 - Column 3 */}
              <div className="form-group">
                <label htmlFor="sponsorPhone">Sponsor Phone</label>
                <input
                  type="tel"
                  id="sponsorPhone"
                  name="sponsorPhone"
                  className="form-input"
                  value={sponsorData.sponsorPhone}
                  onChange={handleSponsorChange}
                  placeholder="Enter sponsor phone number"
                />
              </div>

              {/* Row 3 - Column 1 */}
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="form-input"
                  value={sponsorData.email}
                  onChange={handleSponsorChange}
                  placeholder="Enter email address"
                />
              </div>

              {/* Row 3 - Columns 2 & 3 (full width) */}
              <div className="form-group full-width">
                <label htmlFor="memo">Remarks / Notes</label>
                <textarea
                  id="memo"
                  name="memo"
                  className="form-textarea"
                  value={sponsorData.memo}
                  onChange={handleSponsorChange}
                  placeholder="Enter any additional remarks or notes..."
                  rows="2"
                />
              </div>
            </div>
          </div>

          {/* Form Actions - Back to List moved here */}
          <div className="form-actions">
            <div className="actions-left">
            </div>
            <div className="actions-right">
              <button 
                type="submit" 
                className="btn-save" 
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner-small"></span>
                    Saving...
                  </>
                ) : (
                  '💾 Save Changes'
                )}
              </button>
              <button 
                type="button" 
                className="btn-cancel" 
                onClick={handleCancel}
                disabled={submitting}
              >
                ❌ Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSponsor;